<template>
  <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
      <div class="mt-3">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            {{ campaign ? 'Edit Campaign' : 'Create Campaign' }}
          </h3>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <form @submit.prevent="saveCampaign" class="space-y-4">
          <div>
            <label class="form-label">Campaign Name</label>
            <input
              v-model="form.campaignName"
              type="text"
              class="input-field"
              required
            />
          </div>
          
          <div>
            <label class="form-label">Subject</label>
            <input
              v-model="form.subject"
              type="text"
              class="input-field"
              required
            />
          </div>
          
          <div>
            <label class="form-label">Domain</label>
            <select
              v-model="form.domainID"
              class="input-field"
              required
              @change="onDomainChange"
            >
              <option value="">Select a domain</option>
              <option
                v-for="domain in domains"
                :key="domain.DomainID"
                :value="domain.DomainID"
              >
                {{ domain.DomainName }} (Score: {{ domain.CalculatedScore }})
              </option>
            </select>
          </div>
          
          <div v-if="form.domainID">
            <div class="flex items-center justify-between mb-2">
              <label class="form-label">Template (Optional)</label>
              <button
                type="button"
                @click="syncTemplates"
                :disabled="syncingTemplates"
                class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                <span v-if="syncingTemplates" class="flex items-center">
                  <div class="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></div>
                  Syncing...
                </span>
                <span v-else>Sync from Mailgun</span>
              </button>
            </div>
            <select
              v-model="form.templateID"
              class="input-field"
              :disabled="loadingTemplates"
            >
              <option value="">No template</option>
              <option
                v-for="template in templates"
                :key="template.TemplateID"
                :value="template.TemplateName"
              >
                {{ template.TemplateName }}
                <span v-if="template.Description"> - {{ template.Description }}</span>
              </option>
            </select>
            <p v-if="loadingTemplates" class="text-sm text-gray-500 mt-1">Loading templates...</p>
            <p v-else-if="templates.length === 0 && form.domainID" class="text-sm text-gray-500 mt-1">
              No templates found. Click "Sync from Mailgun" to fetch templates.
            </p>
          </div>
          
          <div>
            <label class="form-label">Email Lists</label>
            <div class="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
              <label
                v-for="list in emailLists"
                :key="list.ListID"
                class="flex items-center space-x-2"
              >
                <input
                  v-model="form.emailListIDs"
                  type="checkbox"
                  :value="list.ListID"
                  class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span class="text-sm text-gray-700">
                  {{ list.ListName }}
                  <span v-if="list.ClubName" class="text-primary-600 text-xs">
                    ({{ list.ClubName }})
                  </span>
                </span>
              </label>
            </div>
          </div>
          
          <div>
            <label class="form-label">Scheduled Start (Optional)</label>
            <div class="flex items-center space-x-2">
              <input
                v-model="form.scheduledStart"
                type="datetime-local"
                class="input-field flex-1"
                :min="minDateTime"
                :max="maxDateTime"
                @change="validateScheduledTime"
              />
              <span class="text-sm text-gray-500 whitespace-nowrap">({{ timezoneLabel }})</span>
            </div>
            <p v-if="scheduledTimeError" class="text-sm text-red-600 mt-1">
              {{ scheduledTimeError }}
            </p>
            <p v-else-if="sendTimeRange" class="text-sm text-gray-500 mt-1">
              Must be within send time range: {{ sendTimeRange.start }} - {{ sendTimeRange.end }} (any date from today onwards). Time is displayed in your local timezone ({{ timezoneLabel }}).
            </p>
          </div>
          
          <div>
            <label class="form-label">From Name (Optional)</label>
            <input
              v-model="form.fromName"
              type="text"
              class="input-field"
              :placeholder="partnerFromName || 'Leave empty to use partner default'"
            />
            <p class="text-sm text-gray-500 mt-1">
              Override the partner's default from name for this campaign. If left empty, the partner's default from name will be used.
            </p>
            <p v-if="partnerFromName" class="text-sm text-gray-400 mt-1">
              Partner default: {{ partnerFromName }}
            </p>
          </div>
          
          <div>
            <label class="form-label">From Email (Optional)</label>
            <input
              v-model="form.fromEmail"
              type="email"
              class="input-field"
              :placeholder="partnerFromEmail || 'Leave empty to use partner default'"
            />
            <p class="text-sm text-gray-500 mt-1">
              Override the partner's default from email for this campaign. If left empty, the partner's default from email will be used.
            </p>
            <p v-if="partnerFromEmail" class="text-sm text-gray-400 mt-1">
              Partner default: {{ partnerFromEmail }}
            </p>
          </div>
          
          <div>
            <label class="form-label">Reply-To Email (Optional)</label>
            <input
              v-model="form.replyToEmail"
              type="email"
              class="input-field"
              :placeholder="partnerReplyToEmail || 'Leave empty to use partner default'"
            />
            <p class="text-sm text-gray-500 mt-1">
              Override the partner's default reply-to email for this campaign. If left empty, the partner's default reply-to email will be used.
            </p>
            <p v-if="partnerReplyToEmail" class="text-sm text-gray-400 mt-1">
              Partner default: {{ partnerReplyToEmail }}
            </p>
          </div>
          
          <div class="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="$emit('close')"
              class="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="btn-primary"
            >
              <span v-if="saving" class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </span>
              <span v-else>{{ campaign ? 'Update' : 'Create' }} Campaign</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { campaignsApi, domainsApi, settingsApi } from '../services/api'
import { toast } from 'vue3-toastify'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { getTimezoneName, formatTimezoneOffset, utcTimeToLocal, utcDateTimeToLocal } from '../utils/timezone'

const props = defineProps({
  campaign: {
    type: Object,
    default: null
  },
  domains: {
    type: Array,
    default: () => []
  },
  emailLists: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'saved'])

// Form state
const form = ref({
  campaignName: '',
  subject: '',
  domainID: '',
  templateID: '',
  emailListIDs: [],
  scheduledStart: '',
  replyToEmail: '',
  fromName: '',
  fromEmail: ''
})

const saving = ref(false)
const templates = ref([])
const loadingTemplates = ref(false)
const syncingTemplates = ref(false)
const settings = ref(null)
const scheduledTimeError = ref('')
const partnerReplyToEmail = ref('')
const partnerFromName = ref('')
const partnerFromEmail = ref('')

// Computed properties for datetime restrictions
const timezoneLabel = computed(() => {
  return getTimezoneName() || formatTimezoneOffset()
})

const sendTimeRange = computed(() => {
  if (!settings.value?.PreferredSendTimeStart || !settings.value?.PreferredSendTimeEnd) {
    return null
  }
  // Convert UTC times from server to local time for display
  const startUtc = settings.value.PreferredSendTimeStart.substring(0, 5) // HH:MM
  const endUtc = settings.value.PreferredSendTimeEnd.substring(0, 5) // HH:MM
  const startLocal = utcTimeToLocal(startUtc) || startUtc
  const endLocal = utcTimeToLocal(endUtc) || endUtc
  return {
    start: startLocal,
    end: endLocal
  }
})

// Helper function to get local date string (YYYY-MM-DD) without timezone conversion
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Helper function to get local datetime string (YYYY-MM-DDTHH:mm) without timezone conversion
const getLocalDateTimeString = (date = new Date()) => {
  const dateStr = getLocalDateString(date)
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${dateStr}T${hours}:${minutes}`
}

// Get the current date part (today or from scheduledStart)
const currentDatePart = computed(() => {
  if (form.value.scheduledStart) {
    return form.value.scheduledStart.split('T')[0]
  }
  // Default to today (local time)
  return getLocalDateString()
})

// Get minimum date (today) for the datetime-local input
const minDate = computed(() => {
  // Use local date, not UTC
  return getLocalDateString()
})

const minDateTime = computed(() => {
  // Always allow from today at 00:00 - let validation handle time range restrictions
  return `${minDate.value}T00:00`
})

const maxDateTime = computed(() => {
  // No max date - allow any future date
  // Time range restrictions are handled by validation, not by max attribute
  return null
})

// Watch for date changes and adjust time if needed
watch(() => form.value.scheduledStart, (newValue) => {
  if (!newValue) return
  
  const [datePart, timePart] = newValue.split('T')
  if (!timePart) return
  
  // Check if the selected date is in the past (using local time)
  const today = new Date()
  const todayStr = getLocalDateString(today)
  
  // Compare dates as strings (YYYY-MM-DD) to avoid timezone issues
  if (datePart < todayStr) {
    // If date is in the past, set to today with the same time
    form.value.scheduledStart = `${todayStr}T${timePart}`
    return
  }
  
  // If no time range, don't validate time
  if (!sendTimeRange.value) return
  
  const scheduledTime = timePart.substring(0, 5) // HH:MM
  const startTime = sendTimeRange.value.start
  const endTime = sendTimeRange.value.end
  
  // Check if time is outside range
  const scheduledTimeObj = new Date(`2000-01-01 ${scheduledTime}:00`)
  const startTimeObj = new Date(`2000-01-01 ${startTime}:00`)
  const endTimeObj = new Date(`2000-01-01 ${endTime}:00`)
  
  let isWithinRange = false
  if (startTimeObj <= endTimeObj) {
    isWithinRange = scheduledTimeObj >= startTimeObj && scheduledTimeObj <= endTimeObj
  } else {
    isWithinRange = scheduledTimeObj >= startTimeObj || scheduledTimeObj <= endTimeObj
  }
  
  // If time is outside range, adjust it to the start time
  if (!isWithinRange) {
    form.value.scheduledStart = `${datePart}T${startTime}`
    validateScheduledTime()
  }
}, { immediate: false })

// Watch for campaign changes to populate form
watch(() => props.campaign, (newCampaign) => {
  if (newCampaign) {
    // Convert UTC datetime from server to local time for display
    let scheduledStartLocal = ''
    if (newCampaign.ScheduledStart) {
      // If it's already in ISO format, convert it
      scheduledStartLocal = utcDateTimeToLocal(newCampaign.ScheduledStart)
    }
    
    form.value = {
      campaignName: newCampaign.CampaignName || '',
      subject: newCampaign.Subject || '',
      domainID: newCampaign.DomainID || '',
      templateID: newCampaign.TemplateID || '',
      emailListIDs: [], // This would need to be populated from campaign lists
      scheduledStart: scheduledStartLocal,
      replyToEmail: newCampaign.ReplyToEmail || '',
      fromName: newCampaign.FromName || '',
      fromEmail: newCampaign.FromEmail || ''
    }
    
    // Set partner defaults from campaign data if available
    if (newCampaign.PartnerReplyToEmail) {
      partnerReplyToEmail.value = newCampaign.PartnerReplyToEmail
    }
    if (newCampaign.PartnerFromName) {
      partnerFromName.value = newCampaign.PartnerFromName
    }
    if (newCampaign.PartnerFromEmail) {
      partnerFromEmail.value = newCampaign.PartnerFromEmail
    }
    // Load templates if domain is set
    if (newCampaign.DomainID) {
      loadTemplates(newCampaign.DomainID)
    }
  } else {
    // Reset form for new campaign
    form.value = {
      campaignName: '',
      subject: '',
      domainID: '',
      templateID: '',
      emailListIDs: [],
      scheduledStart: '',
      replyToEmail: '',
      fromName: '',
      fromEmail: ''
    }
    templates.value = []
  }
}, { immediate: true })

// Methods
const onDomainChange = async () => {
  form.value.templateID = '' // Reset template when domain changes
  if (form.value.domainID) {
    await loadTemplates(form.value.domainID)
  } else {
    templates.value = []
  }
}

const loadTemplates = async (domainId) => {
  if (!domainId) return
  
  try {
    loadingTemplates.value = true
    const response = await domainsApi.getTemplates(domainId)
    templates.value = response.data.data || []
  } catch (error) {
    console.error('Error loading templates:', error)
    templates.value = []
  } finally {
    loadingTemplates.value = false
  }
}

const syncTemplates = async () => {
  if (!form.value.domainID) return
  
  try {
    syncingTemplates.value = true
    await domainsApi.syncTemplates(form.value.domainID)
    toast.success('Templates synced successfully')
    await loadTemplates(form.value.domainID)
  } catch (error) {
    console.error('Error syncing templates:', error)
    // Error toast is handled by API interceptor
  } finally {
    syncingTemplates.value = false
  }
}

const validateScheduledTime = () => {
  scheduledTimeError.value = ''
  
  if (!form.value.scheduledStart || !sendTimeRange.value) {
    return true
  }
  
  // Extract time from datetime-local format (YYYY-MM-DDTHH:mm)
  const scheduledDateTime = new Date(form.value.scheduledStart)
  const scheduledTime = scheduledDateTime.toTimeString().substring(0, 5) // HH:MM
  
  const startTime = sendTimeRange.value.start
  const endTime = sendTimeRange.value.end
  
  // Compare times
  const scheduledTimeObj = new Date(`2000-01-01 ${scheduledTime}:00`)
  const startTimeObj = new Date(`2000-01-01 ${startTime}:00`)
  const endTimeObj = new Date(`2000-01-01 ${endTime}:00`)
  
  let isWithinRange = false
  if (startTimeObj <= endTimeObj) {
    // Normal case: start time is before end time
    isWithinRange = scheduledTimeObj >= startTimeObj && scheduledTimeObj <= endTimeObj
  } else {
    // Edge case: time range spans midnight
    isWithinRange = scheduledTimeObj >= startTimeObj || scheduledTimeObj <= endTimeObj
  }
  
  if (!isWithinRange) {
    scheduledTimeError.value = `Scheduled time must be within the send time range (${startTime} - ${endTime})`
    return false
  }
  
  return true
}

const fetchSettings = async () => {
  try {
    const response = await settingsApi.get()
    if (response.data.success) {
      settings.value = response.data.data
      partnerReplyToEmail.value = response.data.data.ReplyToEmail || ''
      partnerFromName.value = response.data.data.FromName || ''
      partnerFromEmail.value = response.data.data.FromEmail || ''
      console.log('CampaignModal - Settings fetched:', settings.value)
      console.log('CampaignModal - PreferredSendTimeStart:', settings.value?.PreferredSendTimeStart, typeof settings.value?.PreferredSendTimeStart)
      console.log('CampaignModal - PreferredSendTimeEnd:', settings.value?.PreferredSendTimeEnd, typeof settings.value?.PreferredSendTimeEnd)
    }
  } catch (error) {
    console.error('Error fetching settings:', error)
  }
}

const saveCampaign = async () => {
  if (form.value.emailListIDs.length === 0) {
    toast.error('Please select at least one email list')
    return
  }
  
  // Validate scheduled time is within send time range
  if (form.value.scheduledStart && !validateScheduledTime()) {
    return
  }

  try {
    saving.value = true
    
    // Prepare scheduled start data with timezone offset
    let scheduledStartData = null
    if (form.value.scheduledStart) {
      // Get timezone offset in minutes (negative for timezones ahead of UTC)
      const timezoneOffset = new Date().getTimezoneOffset()
      scheduledStartData = {
        datetime: form.value.scheduledStart,
        timezoneOffset: timezoneOffset
      }
    }
    
    if (props.campaign) {
      // Update existing campaign
      await campaignsApi.update(props.campaign.CampaignID, {
        status: 'Draft', // Default status for updates
        scheduledStart: scheduledStartData,
        replyToEmail: form.value.replyToEmail || null,
        fromName: form.value.fromName || null,
        fromEmail: form.value.fromEmail || null
      })
      toast.success('Campaign updated successfully')
    } else {
      // Create new campaign
      await campaignsApi.create({
        campaignName: form.value.campaignName,
        subject: form.value.subject,
        domainID: form.value.domainID,
        templateID: form.value.templateID || null,
        emailListIDs: form.value.emailListIDs,
        scheduledStart: scheduledStartData,
        replyToEmail: form.value.replyToEmail || null,
        fromName: form.value.fromName || null,
        fromEmail: form.value.fromEmail || null
      })
      toast.success('Campaign created successfully')
    }
    
    emit('saved')
  } catch (error) {
    console.error('Error saving campaign:', error)
    toast.error('Failed to save campaign')
  } finally {
    saving.value = false
  }
}

// Lifecycle
onMounted(async () => {
  // Fetch settings to get send time range
  await fetchSettings()
  
  // Don't set a default date - let user choose any date including today
  // Only set default time if no campaign is being edited
  if (!props.campaign && !form.value.scheduledStart) {
    // Set default time to start of send time range (or 9 AM if not set)
    const today = new Date()
    let defaultTime = { hours: 9, minutes: 0 }
    
    if (settings.value?.PreferredSendTimeStart) {
      const startTime = settings.value.PreferredSendTimeStart.substring(0, 5) // HH:MM
      const [hours, minutes] = startTime.split(':')
      defaultTime = { hours: parseInt(hours), minutes: parseInt(minutes) }
    }
    
    // Use local time, not UTC
    form.value.scheduledStart = getLocalDateTimeString(new Date(today.getFullYear(), today.getMonth(), today.getDate(), defaultTime.hours, defaultTime.minutes))
  }
})
</script> 